import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '@/components/Layout';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { BarChart3, Footprints, Users, Calendar, TrendingUp, Activity, AlertTriangle, Download, FileText } from 'lucide-react';
import { PieChart, Pie, Cell, Tooltip as RTooltip, ResponsiveContainer, Legend } from 'recharts';
import PaginationBar, { usePagedRows } from '@/components/PaginationBar';
import { downloadFile } from '@/lib/download';

const API_URL = (window.BACKEND_URL || process.env.REACT_APP_BACKEND_URL || "");

const fmt = (n) => new Intl.NumberFormat('es-UY', { style: 'currency', currency: 'UYU', maximumFractionDigits: 0 }).format(n || 0);
const dateFmt = (s) => (s ? new Date(s + 'T12:00:00').toLocaleDateString('es-UY') : '—');
const daysAgo = (s) => {
  if (!s) return null;
  const d = new Date(s + 'T12:00:00');
  const diff = Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24));
  return diff;
};

// Paleta cohesiva (brand orange + acentos)
const PIE_COLORS = ['#f97316', '#10b981', '#3b82f6', '#a855f7', '#ec4899', '#eab308', '#06b6d4', '#84cc16', '#ef4444', '#6366f1'];

// Exporta filas a XLSX y dispara descarga
function exportToExcel(rows, filename, sheetName = 'Datos') {
  if (!rows || rows.length === 0) return;
  const ws = XLSX.utils.json_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName);
  XLSX.writeFile(wb, filename);
}

// Donut chart wrapper
function DonutCard({ title, data, valueKey, labelKey, valueSuffix = '' }) {
  const total = data.reduce((sum, d) => sum + (d[valueKey] || 0), 0);
  const pieData = data.slice(0, 8).map((d) => ({ name: d[labelKey] || '—', value: d[valueKey] || 0 }));
  return (
    <Card>
      <CardHeader><CardTitle className="text-base">{title}</CardTitle></CardHeader>
      <CardContent>
        {pieData.length === 0 || total === 0 ? (
          <p className="text-sm text-gray-500 text-center py-8">Sin datos</p>
        ) : (
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={pieData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={85}
                  paddingAngle={2}
                  label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                  labelLine={false}
                >
                  {pieData.map((entry, i) => (
                    <Cell key={`cell-${i}`} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <RTooltip formatter={(v) => `${v}${valueSuffix}`} />
                <Legend verticalAlign="bottom" height={28} iconType="circle" iconSize={8} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function Analytics() {
  // Filters
  const today = new Date().toISOString().slice(0, 10);
  const firstOfYear = today.slice(0, 4) + '-01-01';
  const [dateFrom, setDateFrom] = useState(firstOfYear);
  const [dateTo, setDateTo] = useState(today);
  const [scoutFilter, setScoutFilter] = useState('all');

  // Data
  const [invData, setInvData] = useState(null);
  const [trkData, setTrkData] = useState(null);
  const [scouts, setScouts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API_URL}/api/auth/users`)
      .then((res) => setScouts((res.data || []).filter((u) => u.role === 'scout')))
      .catch(() => {});
  }, []);

  const fetchAll = React.useCallback(async () => {
    setLoading(true);
    try {
      const invParams = new URLSearchParams();
      if (dateFrom) invParams.append('date_from', dateFrom);
      if (dateTo) invParams.append('date_to', dateTo);

      const trkParams = new URLSearchParams();
      if (dateFrom) trkParams.append('date_from', dateFrom);
      if (dateTo) trkParams.append('date_to', dateTo);
      if (scoutFilter !== 'all') trkParams.append('scout_id', scoutFilter);

      const [invRes, trkRes] = await Promise.all([
        axios.get(`${API_URL}/api/investments/analytics/inventory?${invParams}`),
        axios.get(`${API_URL}/api/tracking/analytics/summary?${trkParams}`),
      ]);
      setInvData(invRes.data);
      setTrkData(trkRes.data);
    } catch (e) {
      console.error('Analytics fetch error', e);
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, scoutFilter]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  return (
    <Layout>
    <div className="max-w-7xl mx-auto px-4 py-6 space-y-6" data-testid="analytics-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <BarChart3 className="h-7 w-7 text-brand-600" />
            Reportes y Analíticas
          </h1>
          <p className="text-sm text-gray-500 mt-1">Entregas de inventario y actividad de scouting</p>
        </div>
      </div>

      {/* Date filter */}
      <Card>
        <CardContent className="pt-4 pb-4">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <Label className="text-xs font-medium text-gray-600">Desde</Label>
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="mt-1 w-40" data-testid="date-from" />
            </div>
            <div>
              <Label className="text-xs font-medium text-gray-600">Hasta</Label>
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="mt-1 w-40" data-testid="date-to" />
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => { setDateFrom(firstOfYear); setDateTo(today); }}>Este año</Button>
              <Button size="sm" variant="outline" onClick={() => { const d = new Date(); d.setDate(d.getDate() - 30); setDateFrom(d.toISOString().slice(0, 10)); setDateTo(today); }}>Últ. 30d</Button>
              <Button size="sm" variant="outline" onClick={() => { const d = new Date(); d.setDate(d.getDate() - 90); setDateFrom(d.toISOString().slice(0, 10)); setDateTo(today); }}>Últ. 90d</Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="inventory" className="w-full">
        <TabsList>
          <TabsTrigger value="inventory" data-testid="tab-inventory-analytics">
            <Footprints className="h-4 w-4 mr-2" />
            Entregas de Inventario
          </TabsTrigger>
          <TabsTrigger value="scouting" data-testid="tab-scouting-analytics">
            <Activity className="h-4 w-4 mr-2" />
            Scouting
          </TabsTrigger>
        </TabsList>

        {/* ─────────────── INVENTARIO ─────────────── */}
        <TabsContent value="inventory" className="space-y-6">
          {loading ? (
            <Card><CardContent className="p-8 text-center text-gray-400">Cargando…</CardContent></Card>
          ) : !invData || invData.total_units === 0 ? (
            <Card><CardContent className="p-10 text-center text-gray-500">
              <Footprints className="h-10 w-10 mx-auto mb-3 text-gray-300" />
              <p className="font-medium">No hay entregas registradas en el período</p>
              <p className="text-sm mt-1">Registrá inversiones que tomen del inventario y va a aparecer acá.</p>
            </CardContent></Card>
          ) : (
            <>
              {/* Stats cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <StatCard icon={<Footprints />} label="Unidades entregadas" value={invData.total_units} />
                <StatCard icon={<TrendingUp />} label="Monto total" value={fmt(invData.total_amount)} />
                <StatCard icon={<Users />} label="Jugadores beneficiados" value={invData.by_player.length} />
              </div>

              {/* Two donut charts: by provider + by size */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <DonutCard title="Por Marca / Proveedor" data={invData.by_provider} valueKey="units" labelKey="provider" valueSuffix=" u." />
                <DonutCard title="Por Talle" data={invData.by_size} valueKey="units" labelKey="size" valueSuffix=" u." />
              </div>

              {/* Top items */}
              <PaginatedItemsTable rows={invData.by_item} dateFrom={dateFrom} dateTo={dateTo} />

              {/* By player */}
              <PaginatedPlayerInvTable rows={invData.by_player} dateFrom={dateFrom} dateTo={dateTo} />
            </>
          )}
        </TabsContent>

        {/* ─────────────── SCOUTING ─────────────── */}
        <TabsContent value="scouting" className="space-y-6">
          <Card>
            <CardContent className="pt-4 pb-4 flex items-end gap-3">
              <div>
                <Label className="text-xs font-medium text-gray-600">Filtrar por scout</Label>
                <Select value={scoutFilter} onValueChange={setScoutFilter}>
                  <SelectTrigger className="mt-1 w-56" data-testid="scout-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los scouts</SelectItem>
                    {scouts.map((s) => (
                      <SelectItem key={s.id} value={s.id}>{s.full_name || s.username}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="ml-auto">
                <Button
                  variant="outline"
                  onClick={() => downloadFile(`${API_URL}/api/reports/tracking-observations`, {
                    params: {
                      date_from: dateFrom || undefined,
                      date_to: dateTo || undefined,
                      scout_id: scoutFilter !== 'all' ? scoutFilter : undefined,
                    },
                    fallbackName: `observaciones_scouting_${dateFrom}_${dateTo}.xlsx`,
                  })}
                  data-testid="export-observations"
                  title="Excel con los comentarios que dejaron los scouts en cada visita y las notas de las fichas, según el período y scout elegidos"
                >
                  <Download className="h-4 w-4 mr-1" /> Observaciones de scouts (Excel)
                </Button>
              </div>
            </CardContent>
          </Card>

          {loading ? (
            <Card><CardContent className="p-8 text-center text-gray-400">Cargando…</CardContent></Card>
          ) : !trkData || trkData.total_visits === 0 ? (
            <Card><CardContent className="p-10 text-center text-gray-500">
              <Activity className="h-10 w-10 mx-auto mb-3 text-gray-300" />
              <p className="font-medium">No hay visitas registradas en el período</p>
              <p className="text-sm mt-1">Los scouts registran sus visitas desde Seguimiento.</p>
            </CardContent></Card>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <StatCard icon={<Calendar />} label="Visitas totales" value={trkData.total_visits} />
                <StatCard icon={<Activity />} label="Partidos" value={trkData.by_event_type.partido} />
                <StatCard icon={<Activity />} label="Entrenamientos" value={trkData.by_event_type.entrenamiento} />
              </div>

              <PaginatedScoutTable rows={trkData.by_scout} dateFrom={dateFrom} dateTo={dateTo} />

              <PaginatedObservedPlayersTable rows={trkData.by_player} dateFrom={dateFrom} dateTo={dateTo} />

              {(trkData.by_prospect || []).length > 0 && (
                <ProspectsObservedTable rows={trkData.by_prospect} />
              )}
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
    </Layout>
  );
}

function StatCard({ icon, label, value }) {
  return (
    <Card>
      <CardContent className="pt-4 pb-4 flex items-center gap-3">
        <div className="p-2 bg-brand-50 rounded-lg text-brand-600">{React.cloneElement(icon, { className: 'h-5 w-5' })}</div>
        <div>
          <p className="text-xs text-gray-500">{label}</p>
          <p className="text-xl font-bold text-gray-900">{value}</p>
        </div>
      </CardContent>
    </Card>
  );
}

function PaginatedItemsTable({ rows, dateFrom, dateTo }) {
  const { paged, pagination } = usePagedRows(rows, { initialPageSize: 10 });
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-base">Modelos más entregados</CardTitle>
        <Button
          size="sm"
          variant="outline"
          onClick={() => exportToExcel(
            rows.map((it) => ({
              Modelo: it.item_name,
              Proveedor: it.provider !== 'Sin proveedor' ? it.provider : '',
              Talle: it.size !== 'Sin talle' ? it.size : '',
              Categoría: it.category,
              Unidades: it.units,
              Jugadores: it.players_count,
              Monto: it.amount,
            })),
            `inventario_modelos_${dateFrom}_${dateTo}.xlsx`,
            'Modelos'
          )}
          data-testid="export-models"
        >
          <Download className="h-4 w-4 mr-1" /> Excel
        </Button>
      </CardHeader>
      <CardContent>
        {rows.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-4">Sin datos</p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs text-gray-500 uppercase border-b">
                  <tr>
                    <th className="text-left py-2">Modelo</th>
                    <th className="text-left py-2">Proveedor</th>
                    <th className="text-left py-2">Talle</th>
                    <th className="text-right py-2">Unidades</th>
                    <th className="text-right py-2">Jugadores</th>
                    <th className="text-right py-2">Monto</th>
                  </tr>
                </thead>
                <tbody>
                  {paged.map((it, i) => (
                    <tr key={i} className="border-b hover:bg-gray-50">
                      <td className="py-2 font-medium">{it.item_name}</td>
                      <td className="py-2">{it.provider !== 'Sin proveedor' ? it.provider : <span className="text-gray-400 italic">—</span>}</td>
                      <td className="py-2">{it.size !== 'Sin talle' ? it.size : <span className="text-gray-400 italic">—</span>}</td>
                      <td className="py-2 text-right font-semibold">{it.units}</td>
                      <td className="py-2 text-right">{it.players_count}</td>
                      <td className="py-2 text-right">{fmt(it.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <PaginationBar {...pagination} />
          </>
        )}
      </CardContent>
    </Card>
  );
}

function PaginatedPlayerInvTable({ rows, dateFrom, dateTo }) {
  const { paged, pagination } = usePagedRows(rows, { initialPageSize: 25 });
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-base">Entregas por Jugador</CardTitle>
        <Button
          size="sm"
          variant="outline"
          onClick={() => exportToExcel(
            rows.map((p) => ({
              Jugador: p.player_name,
              Unidades: p.units,
              Monto: p.amount,
              Detalle: p.items.map((it) => `${it.units}× ${it.item_name}${it.size !== 'Sin talle' ? ` T-${it.size}` : ''}`).join(' | '),
            })),
            `entregas_por_jugador_${dateFrom}_${dateTo}.xlsx`,
            'Por Jugador'
          )}
          data-testid="export-by-player"
        >
          <Download className="h-4 w-4 mr-1" /> Excel
        </Button>
      </CardHeader>
      <CardContent>
        {rows.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-4">Sin datos</p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs text-gray-500 uppercase border-b">
                  <tr>
                    <th className="text-left py-2">Jugador</th>
                    <th className="text-right py-2">Unidades</th>
                    <th className="text-right py-2">Monto</th>
                    <th className="text-left py-2 pl-4">Detalle</th>
                  </tr>
                </thead>
                <tbody>
                  {paged.map((p) => (
                    <tr key={p.player_id} className="border-b hover:bg-gray-50 align-top">
                      <td className="py-3">
                        <Link to={`/players/${p.player_id}`} className="font-medium text-brand-600 hover:underline">
                          {p.player_name}
                        </Link>
                      </td>
                      <td className="py-3 text-right font-semibold">{p.units}</td>
                      <td className="py-3 text-right">{fmt(p.amount)}</td>
                      <td className="py-3 pl-4">
                        <div className="flex flex-wrap gap-1">
                          {p.items.map((it, i) => (
                            <Badge key={i} variant="outline" className="text-xs">
                              {it.units}× {it.item_name}{it.size !== 'Sin talle' ? ` T-${it.size}` : ''}
                            </Badge>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <PaginationBar {...pagination} />
          </>
        )}
      </CardContent>
    </Card>
  );
}

function PaginatedScoutTable({ rows, dateFrom, dateTo }) {
  const { paged, pagination } = usePagedRows(rows, { initialPageSize: 25 });
  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between">
        <div>
          <CardTitle className="text-base">Actividad por Scout</CardTitle>
          <p className="text-xs text-gray-500">Cuántas visitas hizo cada scout, separado por las que hizo a sus propios representados vs las que hizo a jugadores de otros scouts.</p>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={() => exportToExcel(
            rows.map((s) => ({
              Scout: s.scout_name,
              'Visitas totales': s.visits,
              'A propios': s.own_player_visits || 0,
              'A otros': s.other_player_visits || 0,
              Partidos: s.partidos,
              Entrenamientos: s.entrenamientos,
              'Jugadores distintos': s.unique_players,
              'Última visita': s.last_visit || '',
            })),
            `scouts_actividad_${dateFrom}_${dateTo}.xlsx`,
            'Por Scout'
          )}
          data-testid="export-by-scout"
        >
          <Download className="h-4 w-4 mr-1" /> Excel
        </Button>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs text-gray-500 uppercase border-b">
              <tr>
                <th className="text-left py-2">Scout</th>
                <th className="text-right py-2">Total</th>
                <th className="text-right py-2 text-emerald-700">A propios</th>
                <th className="text-right py-2 text-blue-700">A otros</th>
                <th className="text-right py-2">Partidos</th>
                <th className="text-right py-2">Entren.</th>
                <th className="text-right py-2">Jug. distintos</th>
                <th className="text-right py-2">Última visita</th>
              </tr>
            </thead>
            <tbody>
              {paged.map((s) => (
                <tr key={s.scout_id} className="border-b hover:bg-gray-50">
                  <td className="py-2 font-medium">{s.scout_name}</td>
                  <td className="py-2 text-right font-semibold">{s.visits}</td>
                  <td className="py-2 text-right text-emerald-700 font-semibold">{s.own_player_visits || 0}</td>
                  <td className="py-2 text-right text-blue-700">{s.other_player_visits || 0}</td>
                  <td className="py-2 text-right">{s.partidos}</td>
                  <td className="py-2 text-right">{s.entrenamientos}</td>
                  <td className="py-2 text-right">{s.unique_players}</td>
                  <td className="py-2 text-right text-xs text-gray-600">{dateFmt(s.last_visit)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <PaginationBar {...pagination} />
      </CardContent>
    </Card>
  );
}

function PaginatedObservedPlayersTable({ rows, dateFrom, dateTo }) {
  const { paged, pagination } = usePagedRows(rows, { initialPageSize: 25 });
  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between">
        <div>
          <CardTitle className="text-base">Jugadores observados</CardTitle>
          <p className="text-xs text-gray-500">Total de visitas que recibió cada jugador, separadas entre las hechas por su captador y las hechas por otros scouts. Alerta si pasaron 60+ días sin observación.</p>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={() => exportToExcel(
            rows.map((p) => ({
              Jugador: p.player_name,
              Captador: p.owner_name || '',
              'Visitas totales': p.visits,
              'Por su captador': p.visits_by_owner || 0,
              'Por otros scouts': p.visits_by_others || 0,
              'Scouts distintos': p.unique_scouts,
              'Rating prom.': p.avg_rating != null ? p.avg_rating : '',
              'Última visita': p.last_visit || '',
              'Último scout': p.last_scout || '',
              'Días sin visitas': p.last_visit ? daysAgo(p.last_visit) : '',
            })),
            `jugadores_observados_${dateFrom}_${dateTo}.xlsx`,
            'Jugadores'
          )}
          data-testid="export-by-observed-player"
        >
          <Download className="h-4 w-4 mr-1" /> Excel
        </Button>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs text-gray-500 uppercase border-b">
              <tr>
                <th className="text-left py-2">Jugador</th>
                <th className="text-left py-2">Captador</th>
                <th className="text-right py-2">Total</th>
                <th className="text-right py-2 text-emerald-700">Por captador</th>
                <th className="text-right py-2 text-blue-700">Por otros</th>
                <th className="text-right py-2">Rating prom.</th>
                <th className="text-right py-2">Última visita</th>
                <th className="text-left py-2 pl-3">Último scout</th>
                <th className="text-right py-2">Ficha</th>
              </tr>
            </thead>
            <tbody>
              {paged.map((p) => {
                const d = daysAgo(p.last_visit);
                const stale = d != null && d >= 60;
                return (
                  <tr key={p.player_id} className={`border-b hover:bg-gray-50 ${stale ? 'bg-amber-50/50' : ''}`}>
                    <td className="py-2">
                      <Link to={`/players/${p.player_id}`} className="font-medium text-brand-600 hover:underline">{p.player_name}</Link>
                      {stale && (
                        <span className="ml-2 inline-flex items-center gap-1 text-xs text-amber-700">
                          <AlertTriangle className="h-3 w-3" /> {d}d sin visitas
                        </span>
                      )}
                    </td>
                    <td className="py-2 text-xs text-gray-600">{p.owner_name || <span className="text-gray-400 italic">sin asignar</span>}</td>
                    <td className="py-2 text-right font-semibold">{p.visits}</td>
                    <td className="py-2 text-right text-emerald-700 font-semibold">{p.visits_by_owner || 0}</td>
                    <td className="py-2 text-right text-blue-700">{p.visits_by_others || 0}</td>
                    <td className="py-2 text-right">{p.avg_rating != null ? p.avg_rating : '—'}</td>
                    <td className="py-2 text-right text-xs text-gray-600">{dateFmt(p.last_visit)}</td>
                    <td className="py-2 pl-3 text-xs text-gray-600">{p.last_scout || '—'}</td>
                    <td className="py-2 text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 px-2 text-brand-600"
                        onClick={() => downloadFile(`${API_URL}/api/reports/player-ficha/${p.player_id}`, {
                          fallbackName: `ficha_${(p.player_name || 'jugador').replace(/\s+/g, '_')}.pdf`,
                        })}
                        data-testid={`ficha-pdf-${p.player_id}`}
                        title="Descargar ficha completa (PDF)"
                      >
                        <FileText className="h-4 w-4 mr-1" /> PDF
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <PaginationBar {...pagination} />
      </CardContent>
    </Card>
  );
}


function ProspectsObservedTable({ rows }) {
  const { paged, pagination } = usePagedRows(rows, { initialPageSize: 25 });
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Jugadores de interés observados (no representados)</CardTitle>
        <p className="text-xs text-gray-500">Jugadores que los scouts vieron y no pertenecen a la agencia. Se gestionan desde "Jugadores de interés".</p>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs text-gray-500 uppercase border-b">
              <tr>
                <th className="text-left py-2">Jugador</th>
                <th className="text-left py-2">Club</th>
                <th className="text-left py-2">Estado</th>
                <th className="text-right py-2">Visitas</th>
                <th className="text-right py-2">Rating prom.</th>
                <th className="text-right py-2">Última visita</th>
                <th className="text-left py-2 pl-3">Último scout</th>
              </tr>
            </thead>
            <tbody>
              {paged.map((p) => (
                <tr key={p.prospect_id} className="border-b hover:bg-gray-50">
                  <td className="py-2 font-medium">{p.player_name}</td>
                  <td className="py-2 text-xs text-gray-600">{p.club || '—'}</td>
                  <td className="py-2 text-xs capitalize text-gray-600">{p.status || '—'}</td>
                  <td className="py-2 text-right font-semibold">{p.visits}</td>
                  <td className="py-2 text-right">{p.avg_rating != null ? p.avg_rating : '—'}</td>
                  <td className="py-2 text-right text-xs text-gray-600">{dateFmt(p.last_visit)}</td>
                  <td className="py-2 pl-3 text-xs text-gray-600">{p.last_scout || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <PaginationBar {...pagination} />
      </CardContent>
    </Card>
  );
}
